/**
 * Adaptadores de dados — Re Leve IA
 * Transforma os JSONs brutos da planilha em objetos amigáveis para a UI.
 * Regras de privacidade: nunca expor CPF, telefone, endereço ou nome real.
 */

import { parseBrazilianCurrency, safeText, formatCurrencyBR, getField } from './formatters';

// ── Cliente ──────────────────────────────────────────────────────────────────

export function adaptCustomer(raw) {
  if (!raw) return null;

  const clienteId = safeText(raw.clienteId, null);
  // campo "cliente" é número anônimo — nunca tratar como nome
  const clienteAnonimo = raw.cliente !== undefined ? String(raw.cliente) : '?';

  const segmento = safeText(
    getField(raw, ['segmentoComercial', 'segment', 'segmentoRfm']),
    'Indefinido'
  );

  // displayName: nunca usa o campo "cliente" como nome real
  const displayName = clienteId
    ? `${segmento} · ${clienteId}`
    : `Cliente anônimo ${clienteAnonimo}`;

  const valorTotalAcumulado = parseBrazilianCurrency(
    getField(raw, ['valorTotalAcumulado', 'valorTotal'])
  );
  const ticketMedio = parseBrazilianCurrency(
    getField(raw, ['ticketMedio', 'ticket_medio'])
  );
  const ticketMediano = parseBrazilianCurrency(
    getField(raw, ['ticketMediano', 'ticket_mediano'])
  );
  const maiorPedido = parseBrazilianCurrency(
    getField(raw, ['maiorPedido', 'maior_pedido'])
  );
  const menorPedido = parseBrazilianCurrency(
    getField(raw, ['menorPedido', 'menor_pedido'])
  );

  return {
    id: clienteId || `anon_${clienteAnonimo}`,
    clienteId,
    displayName,
    clienteAnonimo,
    segmento,
    // compatibilidade com código legado
    name: displayName,
    segment: segmento,
    totalPedidos: getField(raw, ['totalDePedidos', 'totalPedidos'], null),
    primeiraCompra: getField(raw, ['primeiraCompra'], null),
    ultimaCompra: getField(raw, ['ultimaCompra'], null),
    diasDesdeUltimaCompra: getField(raw, ['diasDesdeUltimaCompra'], null),
    valorTotalAcumulado,
    valorTotalAcumuladoFormatado: formatCurrencyBR(valorTotalAcumulado),
    ticketMedio,
    ticketMedioFormatado: formatCurrencyBR(ticketMedio),
    ticketMediano,
    ticketMedianoFormatado: formatCurrencyBR(ticketMediano),
    maiorPedido,
    maiorPedidoFormatado: formatCurrencyBR(maiorPedido),
    menorPedido,
    menorPedidoFormatado: formatCurrencyBR(menorPedido),
    hipotesePerfil: safeText(getField(raw, ['hipoteseDePerfil', 'hipotesePerfil'])),
    objetivoProvavel: safeText(getField(raw, ['objetivoProvavel'])),
    propensaoRecompra: safeText(getField(raw, ['propensaoARecompra', 'propensaoRecompra'])),
    propensaoUpsell: safeText(getField(raw, ['propensaoAUpsell', 'propensaoUpsell'])),
    propensaoCrossSell: safeText(getField(raw, ['propensaoACrossSell', 'propensaoCrossSell'])),
    interesseAcompanhamentoCalorico: safeText(
      getField(raw, ['interesseEmAcompCalorico', 'possivelInteresseAcompanhamentoCalorico', 'interesseAcompanhamentoCalorico'])
    ),
    nivelConfianca: safeText(getField(raw, ['nivelDeConfianca', 'nivelConfianca'])),
    fontePrincipal: safeText(getField(raw, ['fontePrincipal'])),
    observacaoParaChatbot: safeText(getField(raw, ['observacaoParaChatbot'])),
    origemDoDado: safeText(getField(raw, ['origemDoDado']), 'Planilha'),
    raw,
  };
}

// ── Cenário ──────────────────────────────────────────────────────────────────

export function adaptScenario(raw) {
  if (!raw) return null;

  // guardrailsAplicados vem como "G03, G04" — transformar em array
  const guardrailsRaw = getField(raw, ['guardrailsAplicados'], '');
  const guardrailsAplicados = guardrailsRaw
    ? String(guardrailsRaw).split(',').map(g => g.trim()).filter(Boolean)
    : [];

  return {
    id: safeText(raw.cenarioId, `CEN_?`),
    cenarioId: safeText(raw.cenarioId),
    clienteId: safeText(raw.clienteId, null),
    segmentoComercial: safeText(raw.segmentoComercial),
    objetivoDoChatbot: safeText(raw.objetivoDoChatbot),
    entradaSimuladaCliente: safeText(
      getField(raw, ['entradaSimuladaDoCliente', 'entradaSimuladaCliente'])
    ),
    respostaEsperadaChatbot: safeText(
      getField(raw, ['respostaEsperadaDoChatbot', 'respostaEsperadaChatbot'])
    ),
    guardrailsAplicados,
    ctaRecomendado: safeText(raw.ctaRecomendado),
    tipoBot: safeText(getField(raw, ['tipoDeBot', 'tipoBot'])),
    comentarioExecutivo: safeText(raw.comentarioExecutivo),
    informacoesObservadas: safeText(raw.informacoesObservadas),
    informacoesInferidas: safeText(raw.informacoesInferidas),
    memoriaChatSimulada: safeText(
      getField(raw, ['memoriaDeChatSimulada', 'memoriaChatSimulada'])
    ),
    raw,
  };
}

// ── Guardrail ─────────────────────────────────────────────────────────────────

export function adaptGuardrail(raw) {
  if (!raw) return null;
  return {
    id: safeText(raw.id),
    categoria: safeText(raw.categoria),
    regra: safeText(raw.regra),
    riscoEvitado: safeText(raw.riscoEvitado),
    exemploEntrada: safeText(
      getField(raw, ['exemploDeEntradaDoUsuario', 'exemploEntrada'])
    ),
    respostaSegura: safeText(
      getField(raw, ['respostaSeguraDoChatbot', 'respostaSegura'])
    ),
    acaoDeterministica: safeText(raw.acaoDeterministica),
    podeResponderSozinho: safeText(raw.podeResponderSozinho),
    encaminharHumano: safeText(raw.encaminharHumano),
    raw,
  };
}

// ── Kit ──────────────────────────────────────────────────────────────────────

export function adaptKit(raw) {
  if (!raw) return null;

  const composicao = Array.isArray(raw.composicao)
    ? raw.composicao
    : typeof raw.composicao === 'string'
      ? raw.composicao.split(';').map(s => s.trim()).filter(Boolean)
      : [];

  const segmentosAlvo = Array.isArray(raw.segmentosAlvo)
    ? raw.segmentosAlvo
    : typeof raw.segmentosAlvo === 'string'
      ? raw.segmentosAlvo.split(',').map(s => s.trim()).filter(Boolean)
      : [];

  const guardrailsRelacionados = Array.isArray(raw.guardrails)
    ? raw.guardrails
    : [];

  return {
    id: safeText(raw.id),
    nome: safeText(raw.nome),
    composicao,
    usoRecomendado: safeText(raw.usoRecomendado),
    ctaSugerido: safeText(raw.ctaSugerido),
    segmentosAlvo,
    faixaCalorica: safeText(getField(raw, ['faixaCaloricaEstimada', 'faixaCalorica'])),
    guardrailsRelacionados,
    raw,
  };
}
